/**
 * This will contain the customer form state and direct the customer across the 3 steps:
 *
 *   1. CustomerDataForm
 *   2. CustomerConsentForm
 *   3. CustomerThanks
 *
 * It will also show the additional info form to the client adviser.
 */
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useReducer,
  useMemo,
  Dispatch
} from "react";
import i18next from "i18next";
import { titleCase } from "change-case";
import { validate } from "objectro";
import useAsync from "react-use/lib/useAsync";
import isoCountries from "i18n-iso-countries";
import isoCountriesEn from "i18n-iso-countries/langs/en.json";
import { I18nMessage, POJO, ConfigurableFormShowOnlyIfFn } from "../../types";
import debug from "../../utils/debug";
import {
  get,
  isString,
  isObject,
  isFunction,
  isArray,
  stripNullProperties
} from "../../utils/helpers";
import { getConfigValue } from "../../config";
import api, { TEST_KEYWORDS } from "../../utils/api";
import { getStoreById } from "../../utils/stores";
import transliterate from "../../utils/transliterate";
import appEvents from "../../utils/appEvents";
import AnalyticsSession from "../../utils/AnalyticsSession";
import { APP_PAGE, actions } from "../../hooks/useAppState";
import useCustomerPage, {
  CustomerPageAction,
  UseCustomerPage,
  DEFAULT_CUSTOMER_PAGE,
  FORM_STEP
} from "../../hooks/useCustomerPage";
import useCustomerDataState, {
  DEFAULT_CUSTOMER_DATA_STATE,
  CustomerDataState,
  SetCustomerDataState
} from "../../hooks/useCustomerDataState";
import useCustomerFormState, {
  DEFAULT_CUSTOMER_FORM_STATE,
  CustomerFormState,
  CustomerFormStateObject,
  SetCustomerFormState
} from "../../hooks/useCustomerFormState";
import useAnalytics from "../../hooks/useAnalytics";
import useT from "../../hooks/useT";
import Modal from "../../components/Modal";
import AppFrame from "../../components/AppFrame";
import Page from "../../components/Page";
import Button from "../../components/Button";
import CustomerLanguageSwitcher from "../CustomerLanguageSwitcher";
import CustomerSummary from "../../components/CustomerSummary";
import CustomerThanks from "../../components/CustomerThanks";
import ConfirmLeaveCustomerPage from "../../components/ConfirmLeaveCustomerPage";
import ConfirmSendEmail from "../../components/ConfirmSendEmail";
import ConfirmSalesAdvisor from "../../components/ConfirmSalesAdvisor";
import ConfigurableForm, {
  DEFAULT_ELEMENTS
} from "../../components/ConfigurableForm";
import CustomerConsentIntro from "../../components/CustomerConsentIntro";
import LoadingScreen from "../../components/LoadingScreen";
import { AppStateContext } from "../App";
import "./CustomerPage.css";
import CustomerDetails from "../../components/CustomerDetails";
import { initialState, DQEReducer } from "../../hooks/useDqeReducer";

// This is just to get TS to work with the initial contexts...
const NOOP = () => {};

const T_SPLIT = "¯¯|¯¯";

isoCountries.registerLocale(isoCountriesEn);

export const CustomerPageContext = createContext<UseCustomerPage>([
  { ...DEFAULT_CUSTOMER_PAGE },
  NOOP as Dispatch<CustomerPageAction>
]);

export const CustomerDataStateContext = createContext<CustomerDataState>([
  { ...DEFAULT_CUSTOMER_DATA_STATE },
  NOOP as SetCustomerDataState
]);

export const CustomerFormStateContext = createContext<CustomerFormState>([
  { ...DEFAULT_CUSTOMER_FORM_STATE },
  NOOP as SetCustomerFormState
]);

export const AnalyticsSessionContext = createContext<
  React.MutableRefObject<AnalyticsSession>
>({ current: ({} as any) as AnalyticsSession });

export const DQEContext = createContext<any>(initialState);

export function AppHeaderLeftContent({ previous }: { previous: Function }) {
  const { t } = useT();
  const [appState] = useContext(AppStateContext);
  const [customerPage, doCustomerPageAction] = useContext(CustomerPageContext);

  // Don't display a button if on the thanks page
  if (
    customerPage.step === FORM_STEP.THANKS ||
    customerPage.step === FORM_STEP.ADVISOR
  ) {
    return null;
  }

  // Display a back button if after the customer data step
  // @TODO fix validation after using the previous button
  if (previous && customerPage.step > FORM_STEP.DATA) {
    return (
      <Button
        icon="arrow-left"
        label={t("general.buttons.back.label", {
          lng: customerPage.language || appState.language
        })}
        className="button-role-back"
        onClick={(event: React.MouseEvent) => {
          event.preventDefault();
          previous();
        }}
      />
    );
  }

  // Otherwise display a cancel button
  return (
    <Button
      icon="arrow-left"
      label={t("general.buttons.cancel.label", {
        lng: customerPage.language || appState.language
      })}
      className="button-role-cancel"
      onClick={(event: React.MouseEvent) => {
        event.preventDefault();
        doCustomerPageAction({
          action: "toggleModal",
          payload: {
            name: "confirmLeave",
            display: true
          }
        });
      }}
    />
  );
}

export function AppHeaderRightContent() {
  return <CustomerLanguageSwitcher />;
}

export function ButtonTermsLink({
  t,
  lang = "en",
  labelTranslationId,
  onClick
}: {
  t: Function;
  lang: string;
  labelTranslationId: string;
  onClick: React.MouseEventHandler;
}) {
  return (
    <Button
      shape="link"
      label={t(labelTranslationId, { lng: lang })}
      className="button-role-view-terms"
      onClick={onClick}
    />
  );
}

export default function CustomerPage() {
  // Shared context
  const { t, superT, i18n } = useT();
  const [appState, doAppStateAction] = useContext(AppStateContext);
  const config = appState.config;
  const [customerPage, doCustomerPageAction] = useCustomerPage({
    language: appState.language,
    loading: true
  });
  const [customerDataState] = useCustomerDataState();
  const [dqeState, DQEDispatch] = useReducer(DQEReducer, initialState);

  //In case autofillValues = true (depands on the maison's config),
  //addressCountry will be filled in automatically as defined in setup
  const prefillValues = config.customise.customer.enablePrefilled
    ? {
        addressCountry:
          customerDataState.addressCountry || appState.storeCountry
      }
    : {};
  let [customerFormState, setCustomerFormState] = useCustomerFormState({
    ...customerDataState,
    ...prefillValues,
    ...getConfigValue(appState.zone, "customise.customer.prefillValues", {}),
    ...stripNullProperties(customerDataState)
  });

  const storeData = useAsync(() => getStoreById(appState.storeId), [
    appState.storeId
  ]);

  const analytics = useAnalytics({
    salesPersonId: String(appState.salesPersonId),
    storeId: String(appState.storeId),
    storeCountry: String(appState.storeCountry) || "UNKNOWN",
    storeName: String(appState.storeName) || "UNKNOWN",
    countryName: isoCountries.getName(String(appState.storeCountry), "en")
  });

  const { step } = customerPage;
  const sharedState = useMemo(
    () => ({
      appState,
      pageState: customerPage,
      formState: customerFormState
    }),
    [appState, customerPage, customerFormState]
  );

  const termsLink = useMemo(
    () => (
      <ButtonTermsLink
        t={t}
        lang={customerPage.language}
        labelTranslationId={
          "customer.consent.sections.termsAndConditions.termsLinkText"
        }
        onClick={event => {
          event.preventDefault();
          doCustomerPageAction({
            action: "toggleModal",
            payload: {
              name: "termsAndConditions",
              display: true
            }
          });
          appEvents.trigger("analytics.event", {
            category: "terms",
            action: "terms_view",
            label: "customer_saw_terms",
            dimensions: {
              salesPersonId: analytics.current.salesPersonId,
              storeId: analytics.current.storeId,
              storeCountry: analytics.current.storeCountry || "UNKNOWN",
              storeName: analytics.current.storeName || "UNKNOWN",
              countryName: analytics.current.countryName
            }
          });
        }}
      />
    ),
    [t, customerPage.language]
  );

  const tFn = useCallback(
    (
      i18nMessage: string | string[] | I18nMessage,
      replaceValues: object
    ): React.ReactNode | React.ReactNodeArray => {
      let i18nOptions: i18next.TOptions = {
        ...customerFormState,
        ...replaceValues,
        lng: customerPage.language,
        brand: config.brand,
        brandContactEmail: get(
          config,
          ["zones", appState.zone, "brandContactEmail"],
          config.brandContactEmail
        ),
        brandContactAddress: get(
          config,
          ["zones", appState.zone, "brandContactAddress"],
          config.brandContactAddress
        ),
        termsLink,
        br: <br />
      };
      let translationId: string | string[] = "";
      let output: React.ReactNode | React.ReactNodeArray = "";

      // String translation ID given
      if (isString(i18nMessage)) {
        translationId = String(i18nMessage);
      }
      // I18nMessage object given, potentially with extra options
      else {
        const _i18nMessage = i18nMessage as I18nMessage;
        const { options = {} } = _i18nMessage;

        if (
          !isString(_i18nMessage.translationId) &&
          !isArray(_i18nMessage.translationId)
        ) {
          throw new Error("Missing valid I18nMessage.translationId");
        }

        // Nothing to translate (empty string or empty array given)
        if (
          !_i18nMessage.translationId ||
          (isString(_i18nMessage.translationId) &&
            !String(_i18nMessage.translationId).trim()) ||
          (isArray(_i18nMessage.translationId) &&
            !_i18nMessage.translationId.length)
        ) {
          return "";
        }

        const {
          defaultValue = "",
          values = {},
          lng = customerPage.language
        } = options;

        translationId = _i18nMessage.translationId;
        i18nOptions = { ...i18nOptions, ...values, lng, defaultValue };
      }

      // Nothing to translate
      if (translationId === "") {
        return "";
      }

      // Attempt using the translator function. Any errors from missing
      // interpolation keywords will be caught and not break the app,
      // but you will still need to address and fix any errors!
      try {
        const translated = superT(translationId, i18nOptions);
        output = translated
          ? isString(translated)
            ? String(translated).trim()
            : translated
          : translationId;
      } catch (error) {
        debug("[CustomerPage] t", {
          i18nMessage,
          translationId,
          i18nOptions
        });
        console.error(error);
        return `Error translating: ${translationId}`;
      }

      return output;
    },
    [t, i18n.language, customerPage.language, customerFormState]
  );

  /**
   * Same as above, however it is using the app's configured language, not the customer's.
   */
  const appTFn = useCallback(
    (
      i18nMessage: string | I18nMessage,
      values?: any
    ): React.ReactNode | React.ReactNodeArray => {
      const _i18nMessage: I18nMessage =
        i18nMessage && !isString(i18nMessage)
          ? (i18nMessage as I18nMessage)
          : {
              translationId: String(i18nMessage)
            };
      _i18nMessage.options = {
        ...get(_i18nMessage, "options"),
        lng: appState.language
      };
      return tFn(_i18nMessage, values);
    },
    [tFn]
  );

  let setStep = (step: number) => {
    // call customerPageReducer
    doCustomerPageAction({
      action: "setStep",
      payload: step
    });
  };

  const toggleModal = (name: string, display: boolean) => {
    doCustomerPageAction({
      action: "toggleModal",
      payload: {
        name: name,
        display: display
      }
    });
  };

  const displayAdditionalInfoModal = (customerFormData: any): boolean => {
    // check if enabled
    if (!config.customise.customer.enableAdditionalInfo) {
      return false;
    }

    // check specific data to decide
    const checkShow = config.customise.customer.checkShowAdditionalInfo;

    const sharedState = {
      appState: appState,
      pageState: customerPage,
      formState: customerFormData
    };

    // - You can use a function which takes the sharedState object as a single param
    if (isFunction(checkShow)) {
      return (checkShow as ConfigurableFormShowOnlyIfFn)(sharedState);
    }

    // - You can use an objectro.ValidationRules object which validates on the sharedState object
    if (isObject(checkShow)) {
      return validate(sharedState, checkShow);
    }

    // - or you can use any other value which will be converted to a boolean
    return !!checkShow;
  };

  const completeCustomerData = async (
    customerFormState: Partial<CustomerFormStateObject>
  ) => {
    // Add extra meta data about the store, sales person ID and
    // the preferred language
    const storeData = await getStoreById(appState.storeId);

    const customerFormData = {
      ...customerFormState,
      salesPerson: appState.salesPersonId,
      creationStore: storeData ? storeData.identifier : appState.storeId,
      preferredLanguage: customerPage.language
    };
    return customerFormData;
  };

  const submitAnalyticsEvents = () => {
    const { form, fields, dimensions, steps } = analytics.current.end();

    appEvents.trigger("analytics.event", {
      category: "journey",
      action: "field_journey",
      label: form.journey.join("/"),
      dimensions
    });

    appEvents.trigger("analytics.event", {
      category: "language",
      action: "language_chosen",
      label: appState.language,
      dimensions
    });

    if (analytics.current.transliteratedField.globalFamilyName) {
      appEvents.trigger("analytics.event", {
        category: "transliterator",
        action: "transliterator_modification",
        label: "family_name_global",
        dimensions
      });
    }

    if (analytics.current.transliteratedField.globalGivenName) {
      appEvents.trigger("analytics.event", {
        category: "transliterator",
        action: "transliterator_modification",
        label: "given_name_global",
        dimensions
      });
    }

    if (analytics.current.transliteratedField.jpKanaFamilyName) {
      appEvents.trigger("analytics.event", {
        category: "transliterator",
        action: "transliterator_modification",
        label: "family_name_kana",
        dimensions
      });
    }

    if (analytics.current.transliteratedField.jpKanaGivenName) {
      appEvents.trigger("analytics.event", {
        category: "transliterator",
        action: "transliterator_modification",
        label: "given_name_kana",
        dimensions
      });
    }

    let listCommunicationMethod = "";
    const communicationMethodsSelected: string[] = [];
    Object.keys(form.listCommunicationMethod).forEach(fieldName => {
      if (form.listCommunicationMethod[fieldName]) {
        if (fieldName === "appsConsent") {
          communicationMethodsSelected.push("SMS-MMS-APPS");
        } else {
          communicationMethodsSelected.push(fieldName);
        }
      }
    });

    if (communicationMethodsSelected.length === 0) {
      listCommunicationMethod = "None";
    } else {
      listCommunicationMethod = communicationMethodsSelected.join("/");
    }

    appEvents.trigger("analytics.event", {
      category: "communication_means",
      action: "communication_means_checked",
      label: listCommunicationMethod,
      dimensions
    });

    appEvents.trigger("analytics.timing", {
      category: "completion_time",
      action: "timing_complete",
      label: "form",
      value: form.totalTimeFocused,
      dimensions
    });

    for (let step in steps) {
      let nameStep = [
        "customer_data_page",
        "consent_page",
        "thank_you_page",
        "advisor_page"
      ][step];
      appEvents.trigger("analytics.timing", {
        category: "total_time",
        action: "timing_complete",
        label: nameStep,
        value: steps[step].totalTime,
        dimensions
      });
    }

    // @NOTE hiding this event for short-term since Camille said
    // it's not entirely needed at the moment
    // appEvents.trigger("analytics.timing", {
    //   category: "idle_time",
    //   action: "timing_complete",
    //   label: "form_idle_time",
    //   value: form.totalIdleTime,
    //   dimensions
    // });

    appEvents.trigger("analytics.event", {
      category: "form_filling",
      action: "filling_percentage",
      label: "form_filling_percentage",
      value: form.fillingpercentage,
      dimensions
    });

    for (let field in fields) {
      if (!fields[field].isEmpty) {
        appEvents.trigger("analytics.timing", {
          category: "completion_time",
          action: "timing_complete",
          label: field,
          value: fields[field].totalTimeFocused,
          dimensions
        });
      }
    }

    appEvents.trigger("analytics.event", {
      category: "auto_complete_service",
      action: "auto_complete_service_used",
      label: "google_maps",
      value: form.useGoogleMapsServiceCount,
      dimensions
    });

    if (form.isAddressModifiedAfterAutoCompletion) {
      appEvents.trigger("analytics.event", {
        category: "auto_complete_service",
        action: "auto_complete_service_used",
        label: "google_maps_modified_after_auto_completion",
        dimensions
      });
    }
  };

  const submitCustomer = async (customerFormData: any) => {
    debug("[CustomerPage] submitCustomer:", customerFormData);
    // Update DQE fields
    customerFormData = { ...customerFormData, ...dqeState };

    doCustomerPageAction({
      action: "toggleLoading",
      payload: true
    });

    // Only send analytics if not sending a special test keyword
    // in the familyName property of the customer.
    if (
      customerFormState.familyName &&
      !TEST_KEYWORDS.includes(customerFormState.familyName.trim())
    ) {
      submitAnalyticsEvents();
    }

    api
      .saveCustomerData(customerFormData)
      .then(result => {
        if (
          result.success &&
          get(config, "customise.customer.enableResultsModal")
        ) {
          debug("[CustomerPage] submitCustomer: result:", result);
          //sharedState.formState
          setCustomerFormState({
            ...customerFormData,
            identifier: get(result, "identifier"),
            barcode: get(result, "barcode")
          });
          DQEDispatch({ type: "RESET" });
          toggleModal("submissionResults", true);
          doCustomerPageAction({
            action: "toggleLoading",
            payload: false
          });
        } else {
          doAppStateAction(actions.setPage(APP_PAGE.ACTIONS));
        }
      })
      .catch(error => {
        console.error(error);
        doAppStateAction(actions.setPage(APP_PAGE.ACTIONS));
      });
  };

  // Event handlers
  /*
   * Move to the next step
   * Customer: DATA -> CONSENT -> THANKS -> (additional modal)
   *   -> (confirm advisor modal) -> (ADVISOR) -> submit
   * Remote: DATA -> (additional modal) -> (ADVISOR) -> (confirm send email modal) -> THANKS -> submit
   */
  const next = async (values: Partial<CustomerFormStateObject>) => {
    debug("[CustomerPage] next: values:", values);
    //debug("[CustomerPage] next: current step:", customerPage.step);
    const newCustomerFormState = {
      ...customerFormState,
      ...values
    };
    setCustomerFormState(newCustomerFormState);
    const enableAdvisorForm = get(
      config,
      "customise.customer.enableAdvisorForm"
    );

    const customerFormData = await completeCustomerData(newCustomerFormState);

    // standard customer
    if (!customerFormState.isRemote) {
      switch (customerPage.step) {
        case FORM_STEP.DATA:
          analytics.current.nextStep(0);
          setStep(FORM_STEP.CONSENT);
          break;

        case FORM_STEP.CONSENT:
          analytics.current.nextStep(1);
          if (get(config, "customise.customer.enableSummary")) {
            setStep(FORM_STEP.SUMMARY);
          } else {
            setStep(FORM_STEP.THANKS);
          }
          break;

        case FORM_STEP.SUMMARY:
          analytics.current.nextStep(1);
          setStep(FORM_STEP.THANKS);
          break;
        case FORM_STEP.THANKS:
          analytics.current.nextStep(2);

          // steps: THANKS -> (additional info modal?) -> (confirm advisor modal?) -> (advisor?) - submit
          // check steps from end to start
          if (customerPage.showConfirmSalesAdvisor) {
            // advisor modal confirmed - go to advisor form page
            toggleModal("confirmSalesAdvisor", false);
            setStep(FORM_STEP.ADVISOR);
          } else if (customerPage.showAdditionalInfo) {
            // additional info modal confirmed
            toggleModal("additionalInfo", false);
            if (enableAdvisorForm) {
              // advisor form enabled - open confirm-advisor modal
              toggleModal("confirmSalesAdvisor", true);
            } else {
              // finish - submit customer
              submitCustomer(customerFormData);
            }
          } else {
            // no active modals
            // check if additional-info modal should be displayed
            if (displayAdditionalInfoModal(customerFormData)) {
              // open additional info modal
              toggleModal("additionalInfo", true);
            } else if (enableAdvisorForm) {
              // finish - submit customer
              toggleModal("confirmSalesAdvisor", true);
            } else {
              // finish - submit customer
              submitCustomer(customerFormData);
            }
          }
          break;

        case FORM_STEP.ADVISOR:
          analytics.current.nextStep(3);
          submitCustomer(customerFormData);
          break;
      }
    } else {
      // remote customer
      switch (customerPage.step) {
        case FORM_STEP.DATA:
          analytics.current.nextStep(0);
          if (customerPage.showConfirmSendEmail) {
            // no advisor form,  send-email confirmed - go to thanks page
            analytics.current.nextStep(0);
            toggleModal("confirmSendEmail", false);
            setStep(FORM_STEP.THANKS);
          } else if (customerPage.showAdditionalInfo) {
            toggleModal("additionalInfo", false);
            // additional info confirmed
            if (enableAdvisorForm) {
              // go to advisor form page
              setStep(FORM_STEP.ADVISOR);
            } else {
              // show confirm send-email modal
              toggleModal("confirmSendEmail", true);
            }
          } else if (displayAdditionalInfoModal(customerFormData)) {
            // open additional info modal
            toggleModal("additionalInfo", true);
          } else if (enableAdvisorForm) {
            // no additional info modal
            setStep(FORM_STEP.ADVISOR);
          } else {
            // no additional info, no advisor, go to thanks page
            toggleModal("confirmSendEmail", true);
          }
          break;

        case FORM_STEP.ADVISOR:
          analytics.current.nextStep(1);

          if (customerPage.showConfirmSendEmail) {
            toggleModal("confirmSendEmail", false);
            setStep(FORM_STEP.THANKS);
          } else {
            toggleModal("confirmSendEmail", true);
          }
          break;

        case FORM_STEP.THANKS:
          analytics.current.nextStep(2);
          submitCustomer(customerFormData);
          break;
      }
    }
  };

  // -- Move to the previous step
  const previous = () => {
    switch (customerPage.step) {
      case FORM_STEP.THANKS:
        setStep(FORM_STEP.CONSENT);
        analytics.current.previousStep(2);
        break;
      case FORM_STEP.SUMMARY:
        setStep(FORM_STEP.CONSENT);
        analytics.current.previousStep(2);
        break;
      case FORM_STEP.CONSENT:
        setStep(FORM_STEP.DATA);
        analytics.current.previousStep(1);
        break;

      case FORM_STEP.DATA:
        toggleModal("confirmLeave", true);
        break;
    }
  };

  const backToBeginning = () => {
    setStep(FORM_STEP.DATA);
  };

  // Terms and conditions data and events
  const handleClickCloseTermsAndConditions = useCallback(
    event => {
      event.preventDefault();
      toggleModal("termsAndConditions", false);
    },
    [doCustomerPageAction]
  );

  let termsUrl = get(config, "customise.customer.termsUrl");
  if (termsUrl) {
    termsUrl = termsUrl.replace("{country}", appState.storeCountry);
    termsUrl = termsUrl.replace("{language}", customerPage.language);
  }

  const closeSubmissionResults = useCallback(
    event => {
      event.preventDefault();
      toggleModal("submissionResults", false);
      doAppStateAction(actions.setPage(APP_PAGE.ACTIONS));
    },
    [doCustomerPageAction]
  );

  // Scroll to the top of the page if the step changed
  useEffect(() => {
    window.scroll(0, 0);
  }, [customerPage.step]);

  // Populate store name
  useEffect(() => {
    appEvents.trigger("analytics.event", {
      category: "form",
      action: "start_form",
      label: "customer_start_form",
      sessionControl: "start", //To force a new GA session each time a form is started
      dimensions: {
        salesPersonId: analytics.current.salesPersonId,
        storeId: analytics.current.storeId,
        storeCountry: analytics.current.storeCountry,
        storeName: analytics.current.storeName,
        countryName: analytics.current.countryName
      }
    });
    // Hide the loading after mounting
    if (customerPage.loading) {
      const turnOffLoading = setTimeout(() => {
        doCustomerPageAction({
          action: "toggleLoading",
          payload: false
        });
      }, 600);

      return () => {
        clearTimeout(turnOffLoading);
      };
    }
  }, [storeData.value]);

  // This will map the form state (which is internally managed within each
  // <ConfigurableForm>) to attributes on the form itself that the CSS can
  // target to apply any visual localisation rules.
  const stateToAttributes = {
    "appState.storeCountry": "data-store-country",
    "formState.addressCountry": "data-customer-country"
  };

  const summaryFields = getConfigValue(
    appState.zone,
    "customise.customer.summaryFields"
  );

  return (
    <AnalyticsSessionContext.Provider value={analytics}>
      <CustomerPageContext.Provider
        value={[customerPage, doCustomerPageAction]}
      >
        <AppFrame
          headerLeftContent={<AppHeaderLeftContent previous={previous} />}
          headerRightContent={<AppHeaderRightContent />}
        >
          <CustomerFormStateContext.Provider
            value={[customerFormState, setCustomerFormState]}
          >
            <DQEContext.Provider value={{ dqeState, DQEDispatch }}>
              <Page name="customer" lang={customerPage.language}>
                {/* @TODO form currently locked to these specific steps */}
                {step === Number(FORM_STEP.DATA) && (
                  <ConfigurableForm
                    id="customer-data-form"
                    initialValues={customerFormState}
                    sharedState={sharedState}
                    formConfig={config.forms.customer[0]}
                    lang={customerPage.language}
                    t={tFn}
                    className="customer-form customer-data-form"
                    stateToAttributes={stateToAttributes}
                    onSubmit={next}
                  />
                )}
                {step === Number(FORM_STEP.CONSENT) && (
                  <ConfigurableForm
                    id="customer-consent-form"
                    initialValues={customerFormState}
                    sharedState={sharedState}
                    formConfig={config.forms.customer[1]}
                    lang={customerPage.language}
                    t={tFn}
                    className="customer-form customer-consent-form"
                    stateToAttributes={stateToAttributes}
                    elements={{ ...DEFAULT_ELEMENTS, CustomerConsentIntro }}
                    onSubmit={next}
                  />
                )}
                {step === Number(FORM_STEP.SUMMARY) && (
                  <CustomerSummary
                    customer={sharedState.formState}
                    summaryFields={summaryFields}
                    loading={customerPage.loading}
                    t={tFn}
                    onComplete={async (event: React.MouseEvent) => {
                      event.preventDefault();
                      next(customerFormState);
                    }}
                    onReturn={async (event: React.MouseEvent) => {
                      event.preventDefault();
                      backToBeginning();
                    }}
                  />
                )}
                {step === Number(FORM_STEP.THANKS) && (
                  <CustomerThanks
                    loading={customerPage.loading}
                    isRemote={customerFormState.isRemote}
                    t={tFn}
                    onComplete={async (event: React.MouseEvent) => {
                      event.preventDefault();

                      // @TODO figure out if should show the Additional Info
                      // and if it should download the transliterated data to
                      // feed into the customerFormState.
                      if (
                        config.customise.customer.enableAdditionalInfo &&
                        config.languages.nonRomanLanguages.includes(
                          customerPage.language
                        )
                      ) {
                        const { givenName, familyName } = customerFormState;
                        let updateCustomerFormState: POJO = {};

                        doCustomerPageAction({
                          action: "toggleLoading",
                          payload: true
                        });

                        switch (customerPage.language) {
                          case "ja":
                            const [
                              [romajiGivenName, romajiFamilyName],
                              [kanaGivenName, kanaFamilyName]
                            ] = await Promise.all([
                              transliterate({
                                input: `${givenName}${T_SPLIT}${familyName}`,
                                from: "ja",
                                to: "romaji"
                              }).then(output => output.split(T_SPLIT)),
                              transliterate({
                                input: `${givenName}${T_SPLIT}${familyName}`,
                                from: "ja",
                                to: "katakana"
                              }).then(output => output.split(T_SPLIT))
                            ]);
                            updateCustomerFormState = {
                              alternateGivenName1: titleCase(romajiGivenName),
                              alternateFamilyName1: titleCase(romajiFamilyName)
                              // alternateGivenName2: kanaGivenName,
                              // alternateFamilyName2: kanaFamilyName
                            };
                            break;

                          case "zh":
                          case "zh-Hant":
                          case "zh-Hans":
                            const [
                              pinyinGivenName,
                              pinyinFamilyName
                            ] = await transliterate({
                              input: `${givenName}${T_SPLIT}${familyName}`,
                              from: "zh"
                            }).then(output => output.split(T_SPLIT));
                            updateCustomerFormState = {
                              alternateGivenName1: titleCase(pinyinGivenName),
                              alternateFamilyName1: titleCase(pinyinFamilyName)
                            };
                            break;

                          default:
                            const [
                              globalGivenName,
                              globalFamilyName
                            ] = await transliterate({
                              input: `${givenName}${T_SPLIT}${familyName}`
                            }).then(output => output.split(T_SPLIT));
                            updateCustomerFormState = {
                              alternateGivenName1: titleCase(globalGivenName),
                              alternateFamilyName1: titleCase(globalFamilyName)
                            };
                            break;
                        }

                        doCustomerPageAction({
                          action: "toggleLoading",
                          payload: false
                        });

                        next({
                          ...customerFormState,
                          ...updateCustomerFormState
                        });
                      } else {
                        next(customerFormState);
                      }
                    }}
                  />
                )}
                {step === Number(FORM_STEP.ADVISOR) && (
                  <ConfigurableForm
                    id="customer-advisor-form"
                    initialValues={customerFormState}
                    sharedState={sharedState}
                    formConfig={config.forms.customer[3]}
                    lang={customerPage.language}
                    t={tFn}
                    className="customer-form customer-advisor-form"
                    stateToAttributes={stateToAttributes}
                    elements={{ ...DEFAULT_ELEMENTS, CustomerConsentIntro }}
                    onSubmit={next}
                  />
                )}
                {customerPage.step < FORM_STEP.THANKS && (
                  <Modal
                    className="confirm-leave"
                    isOpen={customerPage.showConfirmLeave}
                  >
                    <ConfirmLeaveCustomerPage />
                  </Modal>
                )}
                {!!config.forms.customer[2] && (
                  <Modal
                    overlayClassName="additional-info modal-overlay"
                    className="additional-info"
                    isOpen={customerPage.showAdditionalInfo}
                  >
                    <ConfigurableForm
                      id="customer-additional-info-form"
                      initialValues={customerFormState}
                      sharedState={sharedState}
                      formConfig={config.forms.customer[2]}
                      className="customer-form customer-additional-info-form"
                      stateToAttributes={stateToAttributes}
                      onSubmit={next}
                      lang={customerPage.language}
                      t={appTFn}
                    >
                      <div className="configurable-form-controls button-group">
                        <Button
                          type="submit"
                          shape="solid"
                          theme="primary"
                          icon={customerPage.loading ? "loading" : undefined}
                          label={String(
                            appTFn(
                              "customer.additionalInfo.buttons.submit.label"
                            )
                          )}
                          className="button-role-submit"
                          disabled={customerPage.loading}
                          fullWidth
                        />
                      </div>
                    </ConfigurableForm>
                  </Modal>
                )}
                <Modal
                  className="terms-and-conditions"
                  isOpen={customerPage.showTermsAndConditions}
                  allowClose={true}
                  onClose={handleClickCloseTermsAndConditions}
                >
                  <header className="terms-and-conditions-header">
                    <h3 className="terms-and-conditions-title">
                      {superT("termsAndConditions.page.title", {
                        lng: customerPage.language
                      })}
                    </h3>
                    <Button
                      className="button-role-close"
                      icon="close"
                      ariaLabel={t("general.buttons.close.label")}
                      onClick={handleClickCloseTermsAndConditions}
                    />
                  </header>
                  <div className="terms-and-conditions-content">
                    {termsUrl && (
                      <div className="iframe-wrap">
                        <iframe src={termsUrl} title="Data_protection"></iframe>
                      </div>
                    )}
                    {/* Attempt to use translation key for specific zone, otherwise use generic translation key */}
                    {!termsUrl &&
                      superT(
                        [
                          config.customise &&
                          config.customise.termAndConditionInOnePlace &&
                          config.customise.termAndConditionInOnePlace === true
                            ? `termsAndConditions.page.content.fr`
                            : `termsAndConditions.page.content.${appState.zone}`,
                          "termsAndConditions.page.content"
                        ],
                        {
                          lng: customerPage.language,
                          br: <br />
                        }
                      )}
                  </div>
                </Modal>
                <Modal
                  className="confirm-send-email"
                  isOpen={customerPage.showConfirmSalesAdvisor}
                >
                  <ConfirmSalesAdvisor onSubmit={next} />
                </Modal>
                <Modal
                  className="confirm-send-email"
                  isOpen={customerPage.showConfirmSendEmail}
                >
                  <ConfirmSendEmail
                    onSubmit={next}
                    formState={sharedState.formState}
                  />
                </Modal>
                <Modal
                  className="submissionResults"
                  isOpen={customerPage.showSubmissionResults}
                >
                  <CustomerDetails
                    customer={sharedState.formState}
                    columns={summaryFields}
                    t={tFn}
                  />

                  <Button
                    className="button-role-close"
                    theme="primary"
                    icon="close"
                    label={t("general.buttons.close.label")}
                    onClick={closeSubmissionResults}
                  />
                </Modal>
                <LoadingScreen
                  className={!customerPage.loading ? "hide-loading-screen" : ""}
                />
              </Page>
            </DQEContext.Provider>
          </CustomerFormStateContext.Provider>
        </AppFrame>
      </CustomerPageContext.Provider>
    </AnalyticsSessionContext.Provider>
  );
}
